import React from 'react'
import { Modal, Space, Tooltip, Typography } from 'antd'
import moment from 'moment'
import styles from './Hands.module.scss'
import { getBadBranchText, snakeToCamel, toFixed } from '../helpers/helpers'
import { history } from '../_history'
import RANKS from '../combosDrawsTemplate/settings'
import {
	trainerFilters,
	trainerHandLineColumnNames,
	trainerHandsColumnNames,
	uploadFilters,
	uploadHandLineColumnNames,
	uploadHandsColumnNames,
} from './constants'
import { maxEquity, minEquity } from './mockEquity'
import { cardToStr, MAP_CARD } from '../Redux/_actions/helpers'
import classNames from 'classnames'

export const getAliasTooltip = (
	villainAliases,
	heroAliases,
	noAliasLocale,
	noOppLocale,
	noHeroLocal,
	heroAliasLocale,
	oppAliasLocale
) => {
	if (!villainAliases && !heroAliases) {
		return <span>{noAliasLocale}</span>
	}

	return (
		<Space style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
			<Typography>{heroAliasLocale}</Typography>
			{!heroAliases ? (
				<span>{noHeroLocal}</span>
			) : (
				heroAliases.map((item, index) => (
					<Space key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
						<div
							style={{
								borderRadius: '50%',
								width: 10,
								height: 10,
								backgroundColor: item.color,
							}}
						/>
						<span>{item.name}</span>
					</Space>
				))
			)}
			<Typography style={{ marginTop: 8 }}>{oppAliasLocale}</Typography>
			{!villainAliases ? (
				<span>{noOppLocale}</span>
			) : (
				villainAliases.map((item, index) => (
					<Space key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
						<div
							style={{
								borderRadius: '50%',
								width: 10,
								height: 10,
								backgroundColor: item.color,
							}}
						/>
						<span>{item.name}</span>
					</Space>
				))
			)}
		</Space>
	)
}

function getLineTooltip(props, isBadBranch, badBranchType) {
	const { actions } = props

	const toolTipTableBody = []

	for (let i = 0; i < actions.length; i++) {
		let ev_disp = toFixed(actions[i].ev, 2)

		const evFontStyle = ev_disp > 0 ? styles.evPos : classNames(styles.evNeg, ev_disp === 0 ? styles.evZero : '')
		ev_disp = ev_disp > 0 ? `+${ev_disp}` : ev_disp

		toolTipTableBody.push(
			<tr style={actions[i].is_choose ? { opacity: 1.0 } : { opacity: 0.5 }}>
				<td style={{ width: 100 }}>{actions[i].action}</td>
				<td className={styles.valueFont}>
					<div style={{ position: 'relative' }}>
						{toFixed(actions[i].played * 100, 2)}
						<div className={styles.percentPlayed} style={{ width: `${actions[i].played * 100}%` }} />
					</div>
				</td>
				<td className={evFontStyle}>{ev_disp}</td>
			</tr>
		)
	}

	return (
		<div className={styles.cardTooltip}>
			<div className={[styles.evLossTooltip, isBadBranch ? styles.BB : styles.notBB].join(' ')}>
				EV LOSS: {toFixed(props.dif_ev, 3)}
			</div>
			{isBadBranch ? <div className={styles.evLossNotification}>{getBadBranchText(badBranchType)}</div> : null}

			<div style={{ marginTop: 16 }}>
				<table width='100%'>
					<tr>
						<td>Actions</td>
						<td>Played, %</td>
						<td className={styles.evZero}>EV</td>
					</tr>
					{toolTipTableBody}
				</table>
			</div>
		</div>
	)
}

export function getLineString(streetActions) {
	if (!streetActions) {
		return null
	}

	return streetActions.map((action, index) => {
		if (action.is_mistake && !action.is_bad_branch) {
			return (
				<Tooltip
					key={index}
					title={getLineTooltip(action.info, false, action.bad_branch_type)}
					placement='rightTop'
					destroyTooltipOnHide
				>
					<a
						href={`/viewer?id=${action.info.spot_id}&q=${action.info.query}`}
						rel='noreferrer'
						target='_blank'
						className={styles.lineValueMistake}
					>
						{action.act}
					</a>
				</Tooltip>
			)
		}

		if (action.is_mistake && action.is_bad_branch) {
			return (
				<Tooltip
					key={index}
					title={getLineTooltip(action.info, true, action.bad_branch_type)}
					placement='rightTop'
					destroyTooltipOnHide
				>
					<a
						href={`/viewer?id=${action.info.spot_id}&q=${action.info.query}`}
						rel='noreferrer'
						target='_blank'
						className={styles.lineValueBadBranch}
					>
						{action.act}
					</a>
				</Tooltip>
			)
		}

		return (
			<span key={index} className={styles.lineValue}>
				{action.act}
			</span>
		)
	})
}

function getRoomValueFastFilter(roomRequestValue) {
	let roomNameForFastFilter

	switch (roomRequestValue) {
		case 'Pacific':
			roomNameForFastFilter = '888 Poker'
			break
		case 'ppl':
			roomNameForFastFilter = 'PPPoker'
			break
		case 'IPoker_txt':
			roomNameForFastFilter = 'iPoker'
			break
		case 'WPN':
			roomNameForFastFilter = 'Winning Poker'
			break
		default:
			roomNameForFastFilter = roomRequestValue
	}

	return roomNameForFastFilter
}

/*
    Rules for filter communication
*/
export const getFilters = (minStack, maxStack, getLocale) => ({
	gMinTimeStamp: {
		name: 'From',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => moment(v).utc().format('ll'),
	},
	gMaxTimeStamp: {
		name: 'To',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => moment(v).utc().format('ll'),
	},
	heroArray: {
		name: 'Hero',
		opt: true,
		emp: [],
		onRemove: null,
		postProc: (v) => v.length,
	},
	villainArray: {
		name: 'Villain',
		opt: true,
		emp: [],
		onRemove: null,
		postProc: (v) => v.length,
	},
	playersArray: {
		name: 'Players',
		opt: true,
		emp: [],
		onRemove: null,
		postProc: (v) => v.length,
	},
	villainAliases: {
		name: 'Villain aliases',
		opt: true,
		emp: [],
		omRemove: null,
		postProc: (v) => v.length,
	},
	heroAliases: {
		name: 'Hero aliases',
		opt: true,
		emp: [],
		omRemove: null,
		postProc: (v) => v.length,
	},
	playersAliases: {
		name: 'Players aliases',
		opt: true,
		emp: [],
		omRemove: null,
		postProc: (v) => v.length,
	},
	stack: {
		name: 'Stack',
		opt: true,
		emp: [minStack && minStack, maxStack && maxStack],
		onRemove: null,
		postProc: (v) => `${v[0]}-${v[1]}`,
	},
	buyIn: {
		name: 'BuyIn',
		opt: true,
		emp: [0, 1000],
		onRemove: null,
		postProc: (v) => `${v[0]}-${v[1]}`,
	},
	line: {
		name: 'Line',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => v,
	},
	error_street: {
		name: 'Street mistakes',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => getLocale({ component: 'common', section: v.toLowerCase() }),
	},
	jobId: {
		name: 'Job',
		opt: true,
		emp: null,
		onRemove: () => {
			history.push('/hands')
		},
		postProc: (v) => v,
	},
	status: {
		name: 'Status',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: () => getLocale({ component: 'hands', section: 'Сalculated' }),
	},
	filterEV: {
		name: 'EV Loss',
		opt: true,
		emp: false,
		onRemove: false,
		postProc: (v) => `>${v}`,
	},
	is_error: {
		name: 'EV Loss',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => `>${v}`,
	},
	session_id: {
		name: 'Session Id',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => v,
	},
	configuration_id: {
		name: 'Configuration Id',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => v,
	},
	heroPos: {
		name: 'Hero positions',
		opt: true,
		emp: [],
		onRemove: null,
		postProc: (v) => v.join(', '),
	},
	villainPos: {
		name: 'Villain positions',
		opt: true,
		emp: [],
		onRemove: null,
		postProc: (v) => v.join(', '),
	},
	playersPos: {
		name: 'Players positions',
		opt: true,
		emp: [],
		onRemove: null,
		postProc: (v) => v.join(', '),
	},
	handsCount: {
		name: 'Hands Count',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => v,
	},
	villainHandsCount: {
		name: 'Villain hands Count',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => v,
	},
	playersHandsCount: {
		name: 'Players hands count',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => v,
	},
	room: {
		name: 'Room',
		opt: true,
		emp: [],
		onRemove: null,
		postProc: (v) => getRoomValueFastFilter(v[0]),
	},

	source: {
		name: 'Source',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => v,
	},

	touch_threashold: {
		name: 'Hands count',
		opt: true,
		emp: 0,
		onRemove: null,
		postProc: (v) => `>${v}`,
	},

	position: {
		name: 'Position',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => v,
	},

	is_hero: {
		name: 'Hero',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => v,
	},

	is_mistake: {
		name: 'Mistake',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => v,
	},

	street: {
		name: 'Street',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => v[0].toUpperCase() + v.slice(1),
	},

	game_mode: {
		name: 'Game mode',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => v,
	},

	postflop_type: {
		name: 'Postflop type',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => v,
	},

	sortedBy: {
		name: 'Sorted by',
		opt: true,
		emp: null,
		onRemove: null,
		postProc: (v) => {
			if (v) {
				let [name, value] = v.split(' ')

				if (name.includes('Δ')) {
					name = name.replace('Δ', 'diff')
				} else {
					name = snakeToCamel(name)
				}

				return getLocale({ component: 'hands', section: name }) + ' ' + value
			}

			return v
		},
	},
})

export const drawModalMatrix = (filter, mockEquity) => {
	const table = []

	for (let i = 0; i < 13; i++) {
		const row = []
		for (let j = 0; j < 13; j++) {
			let countHand = 0
			let selected = filter.length === 0
			const cell = []
			const cards = []

			const cellText =
				i === j
					? RANKS.RANKS[i] + RANKS.RANKS[j]
					: i > j
					? `${RANKS.RANKS[j] + RANKS.RANKS[i]}s`
					: `${RANKS.RANKS[i] + RANKS.RANKS[j]}o`

			if (filter.includes(cellText)) {
				selected = true
			}

			for (let s1 = 0; s1 < 4; s1++) {
				for (let s2 = 0; s2 < 4; s2++) {
					if (i === j && s1 >= s2) {
						continue
					}
					if (i < j && s1 === s2) {
						continue
					}
					if (i > j && s1 !== s2) {
						continue
					}

					const card1 = (12 - i) * 4 + s1
					const card2 = (12 - j) * 4 + s2
					let hand = 0
					let cardName = ''

					if (card1 < card2) {
						hand = MAP_CARD[card2][card1]
						cardName = cardToStr(card2) + cardToStr(card1)
					} else {
						hand = MAP_CARD[card1][card2]
						cardName = cardToStr(card1) + cardToStr(card2)
					}

					countHand++
					cell.unshift(countHand)

					const card = {
						cardName,
						equity: mockEquity[hand],
					}

					cards.push(card)
				}
			}

			const sumEquityOfCell = cards.reduce((acc, current) => acc + current.equity, 0)
			const cellAvgEquity = +(sumEquityOfCell / cards.length).toFixed(4)

			const proportion = maxEquity - minEquity
			const rangePercent = ((cellAvgEquity - minEquity) / proportion) * 100

			const handFilterModalCell = {
				cellText,
				selected,
				count: cell.length,
				rangePercent,
			}
			row.push(handFilterModalCell)
		}

		table.push(row)
	}

	return table
}

export const countHands = (modalMatrix) =>
	modalMatrix
		.slice()
		.flat()
		.filter((cell) => cell.selected)
		.reduce((accum, current) => current.count + accum, 0)

export const isDuplicatedAlias = (aliasArray, aliasItem) =>
	aliasArray.filter((item) => item.aliasId !== aliasItem.aliasId).length !== aliasArray.length

export const getStackValue = (librariesResponse, id, value, source) => {
	return (
		librariesResponse &&
		librariesResponse.libraries
			.map((item) => item.spots)
			.flat()
			.find((item) => item.spot_id === id)[`${source === 'upload' || id === 111 ? 'parse_' : ''}${value}_stack`]
	)
}

export const openConfirmDeleteModal = (id, removeHand, ok, title, cancel) => {
	Modal.warning({
		title: title,
		centered: true,
		closable: true,
		maskClosable: true,
		cancelText: cancel,
		okText: ok,
		onOk: () => removeHand(id),
		okType: 'danger',
	})
}

export const getCorrectRoomName = (room, rooms, handleAction, row) => {
	let roomFromBackend = room

	if (room === 'WinningPokerV2' || room === 'WPN') {
		roomFromBackend = 'Winning Poker'
	}
	if (room === 'Pacific') {
		roomFromBackend = '888 Poker'
	}
	if (room === 'ppl') {
		roomFromBackend = 'PPPoker'
	}
	if (room === 'IPoker_txt') {
		roomFromBackend = 'iPoker'
	}

	const neededRoom = rooms.find((room) => room.name.toLowerCase() === roomFromBackend.toLowerCase())

	return (
		<Tooltip title={<span>{neededRoom?.name}</span>} destroyTooltipOnHide>
			<img
				src={neededRoom?.icon}
				alt=''
				width={neededRoom?.width}
				height={neededRoom?.height}
				onClick={(event) => handleAction(row, event)}
			/>
		</Tooltip>
	)
}

export const getStack = (spotId, librariesResponse) => {
	if (!librariesResponse) {
		return
	}

	const maxStack =
		librariesResponse &&
		Number(spotId) &&
		librariesResponse?.libraries
			.map((item) => item.spots)
			.flat()
			.find((item) => item.spot_id === Number(spotId)).parse_max_stack

	const minStack =
		librariesResponse &&
		Number(spotId) &&
		librariesResponse?.libraries
			.map((item) => item.spots)
			.flat()
			.find((item) => item.spot_id === Number(spotId)).parse_min_stack

	return [minStack, maxStack]
}

export const getMinMaxStack = (spotId, librariesResponse) => {
	if (!librariesResponse) {
		return
	}

	const maxStack =
		librariesResponse &&
		Number(spotId) &&
		librariesResponse?.libraries
			.map((item) => item.spots)
			.flat()
			.find((item) => item.spot_id === Number(spotId)).max_stack

	const minStack =
		librariesResponse &&
		Number(spotId) &&
		librariesResponse?.libraries
			.map((item) => item.spots)
			.flat()
			.find((item) => item.spot_id === Number(spotId)).min_stack

	return [minStack, maxStack]
}

export function binary(arr, item) {
	let min = 0
	let max = arr.length - 1

	while (min <= max) {
		let mid = Math.floor((min + max) / 2)
		let guess = arr[mid]

		if (guess === item) {
			return mid
		} else if (guess < item) {
			min = mid + 1
		} else {
			max = mid - 1
		}
	}

	return null
}

export const getColumns = (source, columns, tab) => {
	const uploadColumNames = tab === 'hands' ? uploadHandsColumnNames : uploadHandLineColumnNames
	const trainerColumNames = tab === 'hands' ? trainerHandsColumnNames : trainerHandLineColumnNames

	const columnNames = source === 'upload' ? uploadColumNames : trainerColumNames

	return columns.filter(({ dataIndex }) => {
		return columnNames.includes(dataIndex)
	})
}

export const getFiltersNames = (source, items) => {
	const filtersNames = source === 'upload' ? uploadFilters : trainerFilters

	return items.filter(({ name }) => {
		return filtersNames.includes(name)
	})
}

// get positions for current spot
export const getSpotPositions = (libraries, spots) => {
	const allSpotPositions = libraries?.libraries
		.map((item) => item.spots)
		.flat()
		.filter((item) => spots.includes(item.spot_id))
		.map((item) => item.spot_positions)
		.flat()

	return [...new Set(allSpotPositions)]
}

export const customStyles = {
	table: {
		style: {
			background: 'var(--bg_card_color)',
			overflowX: 'auto',
			width: '100%',
			display: 'flex',
		},
	},
	tableWrapper: {
		style: {
			display: 'block',
			height: '100%',
		},
	},
	headRow: {
		style: {
			minHeight: 35,
			borderRight: '1px solid var(--table_border_color)',
			borderLeft: '1px solid var(--table_border_color)',
			borderTop: '1px solid var(--table_border_color)',
		},
	},
	headCells: {
		style: {
			paddingRight: 3,
			paddingLeft: 3,
			borderRight: '1px solid var(--table_border_color)',
			display: 'flex',
			justifyContent: 'center',
			textAlign: 'center',
			fontSize: '14px',
		},
		activeSortStyle: {
			color: '#ffffff',
			'&:hover:not(:focus)': {
				color: '#5a6268!important',
			},
		},
		inactiveSortStyle: {
			'&:focus': {
				outline: 'none',
				color: '#5a6268',
			},
			'&:hover': {
				color: '#5a6268',
			},
		},
	},
	rows: {
		style: {
			borderRight: '1px solid var(--table_border_color)',
			borderLeft: '1px solid var(--table_border_color)',
			minHeight: 35,
			maxHeight: 40,
			'& > *:first-child': {
				padding: 0,
			},
		},
		highlightOnHoverStyle: {
			color: 'white',
			backgroundColor: '#262626',
			transitionDuration: '0.15s',
			transitionProperty: 'background-color',
			outlineStyle: 'solid',
			outlineWidth: '1px',
		},
	},
	cells: {
		style: {
			borderRight: '1px solid var(--table_border_color)',
			display: 'flex',
			justifyContent: 'center',
			paddingRight: 3,
			paddingLeft: 3,
		},
	},
}

export const applyFilterByHands = (modalMatrix, lowPercent, highPercent) => {
	return modalMatrix.map((row) =>
		row.map((cell) => {
			let selected = false

			if (lowPercent <= cell.rangePercent && cell.rangePercent <= highPercent) {
				selected = true
			}

			return { ...cell, selected }
		})
	)
}
